import {
  PublicClientApplication,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { msalConfig } from '../../../authConfig';
import {
  clearActivityTimer,
  hasBeenInactiveForTooLong,
  resetActivityTimer,
} from '../../utils/activityStorage';
import { MsalProvider, useMsal } from '@azure/msal-react';
import AuthManagerContext, { AuthManager } from '../../contexts/authManager';
import { disableNavigationGuard } from '../../hooks/useNavigationGuard';

const InnerAuthManagerProvider = ({ children }: { children?: ReactNode }) => {
  const { instance, inProgress } = useMsal();
  const [hasFinishedInitialProcessing, setHasFinishedInitialProcessing] =
    useState(false);

  const isLoggingOut = useRef(false);

  useEffect(() => {
    if (
      inProgress === InteractionStatus.None &&
      !hasFinishedInitialProcessing
    ) {
      setHasFinishedInitialProcessing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress]);

  useEffect(() => {
    const callbackId = instance.addEventCallback(() => {
      // The activity information is cleared every time there is a successful login or logout.
      // Technically only login is necessary, but this way information isn't kept in storage when not needed.
      clearActivityTimer();
    }, [EventType.LOGIN_SUCCESS, EventType.LOGOUT_SUCCESS]);
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  const contextValue = useMemo<AuthManager | null>(() => {
    if (!hasFinishedInitialProcessing) {
      return null;
    }

    const authExpiredListeners = new Set<() => void>();

    const getAuthBearerToken = async (): Promise<string | null> => {
      const [account] = instance.getAllAccounts();
      if (!account) {
        return null;
      }

      const { idToken, idTokenClaims } = account;
      if (!idToken || !idTokenClaims || typeof idTokenClaims.exp !== 'number') {
        return null;
      }

      // Claims expiration time is set in seconds, so it has to be converted to milliseconds for comparison
      // A new token will try to be retrieved 2 minutes before the actual expiration to evade using an expired token in the gap.
      if (new Date().getTime() < (idTokenClaims.exp - 2 * 60) * 1000) {
        return idToken;
      }

      try {
        const response = await instance.acquireTokenSilent({
          account,
          scopes: ['openid'],
        });
        return response.idToken;
      } catch {
        /* If no new token could be retrieved, that means the current token has completely expired and the session has finished, 
         all listeners are invoked */
        authExpiredListeners.forEach(listener => listener());
        return null;
      }
    };

    return {
      requestLoginRedirect: () =>
        disableNavigationGuard(() => instance.loginRedirect()),
      requestLogoutRedirect: redirectUri => {
        if (isLoggingOut.current) {
          return Promise.resolve();
        }
        isLoggingOut.current = true;
        return disableNavigationGuard(() => {
          const accounts = instance.getAllAccounts();
          if (accounts.length > 0) {
            const idToken = accounts[0].idToken;
            return instance
              .logoutRedirect({
                postLogoutRedirectUri: redirectUri,
                idTokenHint: idToken,
              })
              .catch(
                () =>
                  // The promise doesn't need to be resolved as the page will reload
                  new Promise(() => {
                    window.location.assign(redirectUri);
                  }),
              );
          }
          return new Promise(() => {
            // The promise doesn't need to be resolved as the page will reload
            window.location.assign(redirectUri);
          });
        });
      },
      requestAuthExpirationCheck: () => getAuthBearerToken().then(() => {}),
      getAuthBearerToken,
      reportAuthActivity: resetActivityTimer,
      hasAuthBeenInactiveForTooLong: hasBeenInactiveForTooLong,
      addAuthExpirationListener: handler => {
        authExpiredListeners.add(handler);
      },
      removeAuthExpirationListener: handler => {
        authExpiredListeners.delete(handler);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFinishedInitialProcessing]);

  if (!hasFinishedInitialProcessing || !contextValue) {
    return null;
  }

  return (
    <AuthManagerContext.Provider value={contextValue}>
      {children}
    </AuthManagerContext.Provider>
  );
};

const AuthManagerProvider = ({ children }: { children?: ReactNode }) => {
  const [msalInstance, setMsalInstance] =
    useState<PublicClientApplication | null>(null);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        const instance = new PublicClientApplication(msalConfig);

        await instance.initialize();

        setMsalInstance(instance);
      } catch (error) {
        console.error('MSAL initialization failed:', error);
      }
    };

    void initializeMsal();
  }, []);

  if (!msalInstance) {
    return null; // Prevent rendering until MSAL is initialized
  }

  return (
    <MsalProvider instance={msalInstance}>
      <InnerAuthManagerProvider>{children}</InnerAuthManagerProvider>
    </MsalProvider>
  );
};

export default AuthManagerProvider;
